import { SphereTooltip } from "@components/common/sphere-tooltip";
import { MemberHeaders } from "@components/table/members/members-table-utils";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { BaseMemberProps } from "@custom-types/member-types";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { useMembersUtils } from "@hooks/use-member-utils";
import { Box } from "@mui/material";
import { withEllipsis } from "@styles/common-styles";
import { isTeam } from "@utils/team-utils";
import { DASH } from "@utils/ui-utils";

interface Props extends BaseMemberProps {
  /** The type of column to show the information */
  columnType: MemberHeaders.user | MemberHeaders.email | MemberHeaders.name;
}

/** Shows the basic information in a member column */
export function BasicInfoMemberColumn({
  member,
  columnType,
}: Props): JSX.Element {
  const { handleErrorWithToast } = useErrorContext();
  const { openProfilePage } = useMembersUtils();
  const { canViewCompanyMemberProfile } = useHasUserValidRoleCompanyLevel({
    memberId: member?.id,
  });

  let entityValue: string;

  switch (columnType) {
    case MemberHeaders.email:
      entityValue = member.email;
      break;

    case MemberHeaders.user:
    case MemberHeaders.name:
      entityValue = member.name ?? DASH;
      break;

    default:
      entityValue = DASH;
      handleErrorWithToast({
        id: `BasicInfoMemberColumn-${Date.now().toString()}`,
        title: `Unexpected columnType: ${columnType}`,
        error: `Unexpected columnType: ${columnType}`,
      });
      break;
  }

  const entityProp = <var>{entityValue}</var>;

  if (isTeam(member)) {
    if (columnType === MemberHeaders.name) {
      return (
        <SphereTooltip title={entityProp} shouldShowTooltip>
          {entityProp}
        </SphereTooltip>
      );
    }
    if (columnType === MemberHeaders.email) {
      return <var>{DASH}</var>;
    }
  }

  const isTooltipVisible =
    typeof entityValue === "string" &&
    entityValue.trim() !== "" &&
    entityValue !== DASH;

  // If the user doesn't have permission to view the profile, the text is not clickable.
  if (!canViewCompanyMemberProfile) {
    return (
      <SphereTooltip title={entityProp} shouldShowTooltip={isTooltipVisible}>
        {entityProp}
      </SphereTooltip>
    );
  }

  return (
    <SphereTooltip
      title={entityProp}
      boxProps={{
        sx: {
          ...withEllipsis,
          fontWeight: columnType === MemberHeaders.email ? "normal" : "bold",
        },
      }}
      shouldShowTooltip={isTooltipVisible}
    >
      <Box
        component="var"
        sx={{
          cursor: "pointer",
          "&:hover": {
            textDecoration: "underline",
          },
        }}
        onClick={(event: React.MouseEvent) => {
          // Since this button is normally shown inside a table row, we need to stop the propagation
          // otherwise it will count the click on the row.
          event.stopPropagation();
          openProfilePage({
            member,
            source: `user ${columnType} column`,
          });
        }}
      >
        {entityProp}
      </Box>
    </SphereTooltip>
  );
}
